// services/rankingService.js
import { db } from "firebase.js";
import { collection, addDoc } from "firebase/firestore";
import {
  query,
  where,
  orderBy,
  getDocs,
  serverTimestamp,
  doc,
  updateDoc,
  deleteDoc,
  deleteField,
  writeBatch
} from "firebase/firestore";
import { getMatchesByTournamentId } from "services/matchService";
import * as Sentry from "@sentry/react";

export const getLocationRankings = async (locationId) => {
  const q = query(
    collection(db, "locationRankings"),
    where("locationId", "==", locationId),
    orderBy("ranking", "desc")
  );

  const snapshot = await getDocs(q);

  console.log(`Firestore read count: ${snapshot.docs.length}`);

  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  }));
};

export const getLocationRanking = async (userId, locationId) => {
  if (!userId || !locationId) return null;

  const q = query(
    collection(db, "locationRankings"),
    where("userId", "==", userId),
    where("locationId", "==", locationId)
  );

  const snapshot = await getDocs(q);
  if (snapshot.empty) return null;

  return snapshot.docs[0].data(); // assume only one per user/location
};

/**
 * Create location ranking documents in Firestore
 * @param {string} locationId - ID of the location
 * @param {string} csvData - Raw CSV string
 */
export const importLocationRankings = async (locationId, csvData) => {
  // Step 1: Delete existing rankings for the location
  const locationRankingsRef = collection(db, "locationRankings");
  const q = query(locationRankingsRef, where("locationId", "==", locationId));
  const existingDocs = await getDocs(q);

  const deletePromises = existingDocs.docs.map((doc) => deleteDoc(doc.ref));
  await Promise.all(deletePromises);

  // Step 2: Parse and import new rankings from CSV
  const lines = csvData.trim().split("\n");

  const createPromises = lines.map(async (line) => {
    const [playerName, rankingStr] = line.split(",");
    const ranking = parseInt(rankingStr, 10);

    if (!playerName || isNaN(ranking)) return;

    await addDoc(locationRankingsRef, {
      locationId: locationId,
      playerName: playerName.trim(),
      ranking: ranking,
      createdAt: serverTimestamp(),
    });
  });

  await Promise.all(createPromises);
};

export const updateLocationRanking = async (id, updates) => {
  const ref = doc(db, "locationRankings", id);
  await updateDoc(ref, updates);
};

const ELO_K = 30;
const ELO_DIVISIONS = 800;

// --- 1. Elo calculation ---
const calculateEloDelta = (team1Avg, team2Avg, team1Score, team2Score) => {
  const expected1 = 1 / (1 + Math.pow(10, (team2Avg - team1Avg) / ELO_DIVISIONS));
  const expected2 = 1 / (1 + Math.pow(10, (team1Avg - team2Avg) / ELO_DIVISIONS));

  const actual1 = team1Score > team2Score ? 1 : 0;
  const actual2 = team2Score > team1Score ? 1 : 0;

  const deltaTeam1 = Math.round(ELO_K * (actual1 - expected1));
  const deltaTeam2 = Math.round(ELO_K * (actual2 - expected2));

  return { deltaTeam1, deltaTeam2 };
};

// --- 2. Build player match history and update matches ---
const processMatches = async (matches, batch) => {
  const playerMatches = {};

  for (const match of matches) {
    if (!match.result) continue;
    if (match.team1Name === "BYE" || match.team2Name === "BYE") continue;

    const { team1, team2, result } = match;
    // console.log(team1, match.team1Name, match.team2Name, match.id, match.nextMatchId, match.phase);
    const team1Score = result.team1;
    const team2Score = result.team2;

    if (team1Score === team2Score) continue;

    const team1PlayerIds = team1.players.map((p) => p.id);
    const team2PlayerIds = team2.players.map((p) => p.id);

    const team1RankingAvg = team1.players.reduce((sum, p) => sum + (p.ranking ?? 1000), 0) / team1.players.length;
    const team2RankingAvg = team2.players.reduce((sum, p) => sum + (p.ranking ?? 1000), 0) / team2.players.length;

    const { deltaTeam1, deltaTeam2 } = calculateEloDelta(team1RankingAvg, team2RankingAvg, team1Score, team2Score);

    // Update match with point differences
    const matchRef = doc(db, "matches", match.id);
    batch.update(matchRef, {
      points: {
        team1: deltaTeam1,
        team2: deltaTeam2,
      },
      updatedAt: new Date().toISOString(),
    });

    // Build player matches
    [...team1PlayerIds, ...team2PlayerIds].forEach((playerId) => {
      if (!playerMatches[playerId]) playerMatches[playerId] = [];
      playerMatches[playerId].push({
        matchId: match.id,
        delta: team1PlayerIds.includes(playerId) ? deltaTeam1 : deltaTeam2,
      });
    });
  }

  return playerMatches;
};

// --- 3. Fetch player rankings ---
const fetchPlayerRankings = async (locationId, playerIds) => {
  const locationRankings = {};

  for (let i = 0; i < playerIds.length; i += 10) {
    const q = query(
      collection(db, "locationRankings"),
      where("locationId", "==", locationId),
      where("userId", "in", playerIds.slice(i, i + 10))
    );

    const snapshot = await getDocs(q);
    snapshot.forEach((docSnap) => {
      const data = docSnap.data();
      locationRankings[data.userId] = {
        id: docSnap.id,
        ranking: data.ranking ?? 1000,
      };
    });
  }

  return locationRankings;
};

// --- 4. Update player rankings ---
const updatePlayerRankings = (batch, tournament, playerMatches, locationRankings) => {
  for (const [playerId, matches] of Object.entries(playerMatches)) {
    const totalDelta = matches.reduce((sum, m) => sum + m.delta, 0);

    const existing = locationRankings[playerId];

    if (existing) {
      const rankingRef = doc(db, "locationRankings", existing.id);
      batch.update(rankingRef, {
        ranking: existing.ranking + totalDelta,
        updatedAt: new Date().toISOString(),
      });
    } else {
      const rankingRef = doc(collection(db, "locationRankings"));
      batch.set(rankingRef, {
        locationId: tournament.locationId,
        tournamentId: tournament.id,
        userId: playerId,
        ranking: 1000 + totalDelta,
        createdAt: new Date().toISOString(),
      });
    }
  }
};

// --- 5. Final step: mark tournament compiled ---
const markTournamentCompiled = (batch, tournament) => {
  const tournamentRef = doc(db, "tournaments", tournament.id);
  batch.update(tournamentRef, {
    compiled: true,
    compiledAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  });
};

// --- Revert compilation ---
export const revertTournamentCompilation = async (tournament) => {
  if (!tournament?.id || !tournament?.locationId) {
    throw new Error("Tournoi invalide.");
  }

  // Safety check: block revert if a more recent tournament was compiled at this location
  const tournamentsQuery = query(
    collection(db, "tournaments"),
    where("locationId", "==", tournament.locationId),
    where("compiled", "==", true)
  );
  const tournamentsSnap = await getDocs(tournamentsQuery);
  for (const docSnap of tournamentsSnap.docs) {
    const data = docSnap.data();
    if (docSnap.id !== tournament.id && data.compiledAt && tournament.compiledAt && data.compiledAt > tournament.compiledAt) {
      throw new Error("Un tournoi plus récent a été compilé à cet emplacement. Annulez celui-là d'abord.");
    }
  }

  // Extract player rankings from tournament.teams
  const playerRankings = {};
  if (tournament.teams) {
    for (const division of Object.values(tournament.teams)) {
      for (const team of division) {
        if (team.players) {
          for (const player of team.players) {
            const playerId = player.userId || player.id;
            if (playerId) {
              playerRankings[playerId] = player.ranking ?? 0;
            }
          }
        }
      }
    }
  }

  const playerIds = Object.keys(playerRankings);
  if (!playerIds.length) {
    throw new Error("Aucun joueur trouvé dans les équipes du tournoi.");
  }

  const locationRankings = await fetchPlayerRankings(tournament.locationId, playerIds);

  const batch = writeBatch(db);

  // Restore player rankings
  for (const [playerId, teamsRanking] of Object.entries(playerRankings)) {
    const existing = locationRankings[playerId];
    if (!existing) continue;

    if (teamsRanking > 0) {
      const rankingRef = doc(db, "locationRankings", existing.id);
      batch.update(rankingRef, {
        ranking: teamsRanking,
        updatedAt: new Date().toISOString(),
      });
    } else {
      // Player had no ranking before compilation — delete the doc
      const rankingRef = doc(db, "locationRankings", existing.id);
      batch.delete(rankingRef);
    }
  }

  // Remove points from matches
  const matches = await getMatchesByTournamentId(tournament.id);
  for (const match of matches) {
    if (match.points) {
      const matchRef = doc(db, "matches", match.id);
      batch.update(matchRef, {
        points: deleteField(),
        updatedAt: new Date().toISOString(),
      });
    }
  }

  // Reset tournament
  const tournamentRef = doc(db, "tournaments", tournament.id);
  batch.update(tournamentRef, {
    compiled: false,
    compiledAt: deleteField(),
    updatedAt: new Date().toISOString(),
  });

  await batch.commit();
};

// --- MAIN function ---
export const compileTournamentElo = async (tournament, compile=false) => {
  try {
    if (!tournament?.id || !tournament?.locationId) return;

    const matches = await getMatchesByTournamentId(tournament.id);
    if (!matches.length) return;

    const batch = writeBatch(db);

    const playerMatches = await processMatches(matches, batch);
    if (!compile) return playerMatches;
    
    const playerIds = Object.keys(playerMatches);
    if (!playerIds.length) return;

    const locationRankings = await fetchPlayerRankings(tournament.locationId, playerIds);
    updatePlayerRankings(batch, tournament, playerMatches, locationRankings);
    markTournamentCompiled(batch, tournament);

    await batch.commit();
  } catch (error) {
    console.error("❌ Failed to compile tournament Elo:", error);
    Sentry.captureException(error);
  }
};

